import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * ActivityIndicator — inline "live status" row shown in the assistant line while
 * the bot is working, BEFORE/DURING the answer text. Reduces the AG-UI backend
 * event stream (RUN_STARTED / REASONING / STEP / TOOL_CALL / TEXT_MESSAGE /
 * RUN_FINISHED / RUN_ERROR) to a short label + live spinner.
 *
 * Absorbs TypingIndicator: the classic 3-dot is the `glyph="dots"` treatment of
 * the `writing` status. On RUN_FINISHED the caller unmounts this and renders the
 * real MessageBubble instead. Token-only; adapts to preset / light-dark /
 * roundness + density knobs. Motion is neutralized by base.css reduced-motion.
 */

export type ActivityStatus =
  | 'starting' // RUN_STARTED
  | 'thinking' // REASONING_START / THINKING_START (+ *_CONTENT)
  | 'step' // STEP_STARTED (use `detail` for the step name) / STEP_FINISHED
  | 'tool' // TOOL_CALL_START (use `detail` for the tool name) / TOOL_CALL_ARGS
  | 'toolResult' // TOOL_CALL_RESULT / TOOL_CALL_END
  | 'writing' // TEXT_MESSAGE_START / TEXT_MESSAGE_CONTENT
  | 'done' // step/tool/reasoning finished — collapsed static trace line
  | 'aborted' // RUN_ERROR (code=aborted)
  | 'error'; // RUN_ERROR (other)

export interface ActivityLabelSpec {
  /** Label template; `{detail}` is replaced with the `detail` prop. */
  text: string;
  /** Used when no `detail` is supplied (e.g. "도구 실행 중…"). */
  fallback?: string;
  /** Duration unit for the `done` trace ("s" or "초"). */
  unit?: string;
}

/** Default Korean microcopy (neutral-polite). Override per preset for tone. */
export const ACTIVITY_LABELS: Record<ActivityStatus, ActivityLabelSpec> = {
  starting: { text: '시작하는 중…' },
  thinking: { text: '생각하는 중…' },
  step: { text: '단계: {detail}', fallback: '단계 진행 중…' },
  tool: { text: '도구 실행: {detail}', fallback: '도구 실행 중…' },
  toolResult: { text: '결과 처리 중…' },
  writing: { text: '답변 작성 중…' },
  done: { text: '{detail} 사용함', fallback: '완료', unit: 's' },
  aborted: { text: '중단됨' },
  error: { text: '오류가 발생했어요' },
};

/** Format a duration for the `done` trace. `value` = seconds; unit "s" or "초". */
export function formatDuration(value?: number | string | null, unit = 's'): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return String(value);
  const txt = n >= 10 ? String(Math.round(n)) : n.toFixed(1);
  return unit === '초' ? `${txt}초` : txt + (unit || 's');
}

/**
 * Live elapsed-time readout for an in-progress activity. Ticks up from `startedAt`
 * (ms epoch) so a running tool/step shows "· 2.4s" counting in real time, matching
 * the final stamped duration once the block settles to `done`. ~100ms cadence for
 * a smooth tenths display; cheap (typically one active row at a time, and only
 * this leaf re-renders). Not decorative, so it keeps ticking under reduced-motion.
 */
export function LiveDuration({
  startedAt,
  unit = 's',
  className,
}: {
  startedAt: number;
  unit?: string;
  className?: string;
}) {
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => tick((n) => (n + 1) % 1_000_000), 100);
    return () => clearInterval(id);
  }, []);
  const secs = Math.max(0, (Date.now() - startedAt) / 1000);
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex-none font-mono text-[length:var(--text-xs)] tabular-nums text-[var(--color-status-done-fg)]',
        className,
      )}
    >
      · {formatDuration(secs, unit)}
    </span>
  );
}

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Per-status Lucide-shaped glyph (used when glyph="icon" or on error/aborted). */
const ICONS: Record<ActivityStatus, React.ReactNode> = {
  starting: <circle cx="12" cy="12" r="9" strokeDasharray="3.5 4" />,
  thinking: (
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  ),
  step: (
    <>
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </>
  ),
  tool: <path d="M14.7 6.3a4 4 0 0 0-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 0 0 5.2-5.2l-2.9 2.9-2.1-2.1z" />,
  toolResult: <path d="M20 6 9 17l-5-5" />,
  done: <path d="M20 6 9 17l-5-5" />,
  writing: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ),
  aborted: (
    <>
      <circle cx="12" cy="12" r="9" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </>
  ),
  error: (
    <>
      <path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
};

const ICON_SIZE = 'size-[var(--status-icon-size)]';

function Spinner({ colorClass }: { colorClass: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn(ICON_SIZE, 'block animate-[lb-spin_0.8s_linear_infinite]', colorClass)}
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function Dots() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex w-[var(--status-icon-size)] justify-center gap-1 text-[var(--color-status-icon)]"
    >
      {[0, 0.15, 0.3].map((delay) => (
        <span
          key={delay}
          className="size-1.5 rounded-full bg-current animate-[lb-bounce_1.2s_infinite]"
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
    </span>
  );
}

function Icon({ glyphKey, colorClass }: { glyphKey: ActivityStatus; colorClass: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...STROKE} className={cn(ICON_SIZE, 'block', colorClass)}>
      {ICONS[glyphKey] ?? ICONS.thinking}
    </svg>
  );
}

export interface ActivityIndicatorProps {
  /** Current activity state (mapped from the AG-UI event stream). */
  status?: ActivityStatus;
  /** Explicit label override; skips the `labels`/`detail` resolution. */
  label?: string;
  /** Variable tool/step name — interpolated into the label and truncated. */
  detail?: string;
  /** Elapsed time in seconds — rendered as a trailing "· 1.2s" on the `done` trace. */
  duration?: number | string;
  /**
   * ms epoch the activity started. When set on a NON-done row, a live "· 2.4s"
   * timer ticks up in real time (settles to `duration` once done). Ignored on
   * done/error rows.
   */
  startedAt?: number;
  /** Original activity kind — its icon is shown on `done` when `glyph="icon"`. */
  kind?: ActivityStatus;
  /** Chrome around the row: bot-bubble shell / bare inline / accent-soft pill. */
  variant?: 'bubble' | 'bare' | 'pill';
  /** Left glyph: rotating spinner / per-state icon / classic 3-dot (writing). */
  glyph?: 'spinner' | 'icon' | 'dots';
  /** Assistant avatar/logo slot, aligned with MessageBubble. */
  avatar?: React.ReactNode;
  /** Override the microcopy map (i18n / per-preset tone). */
  labels?: Record<ActivityStatus, ActivityLabelSpec>;
  className?: string;
}

/**
 * Inline live-status indicator for streaming assistant turns. Compact row of
 * spinner/icon + a single-line (ellipsis-truncated) status label with a polite
 * live region; swaps smoothly as the run progresses.
 */
export function ActivityIndicator({
  status = 'thinking',
  label,
  detail,
  duration,
  startedAt,
  kind,
  variant = 'bubble',
  glyph = 'spinner',
  avatar = null,
  labels = ACTIVITY_LABELS,
  className,
}: ActivityIndicatorProps) {
  const isError = status === 'error' || status === 'aborted';
  const isDone = status === 'done';

  // Resolve label (explicit prop wins; else template with detail; else fallback).
  const spec = labels[status] ?? labels.thinking;
  let text = label;
  if (text == null) {
    text = detail
      ? spec.text.replace('{detail}', detail)
      : (spec.fallback ?? spec.text.replace('{detail}', '').trim());
  }
  const durText = isDone ? formatDuration(duration, spec.unit) : '';

  const iconColor = isError
    ? 'text-[var(--color-status-error-fg)]'
    : isDone
      ? 'text-[var(--color-status-done-icon)]'
      : 'text-[var(--color-status-icon)]';

  let Glyph: React.ReactNode;
  if (isDone) {
    // done: static ✓ (or the original kind's icon when glyph="icon"), never a spinner
    Glyph = <Icon glyphKey={glyph === 'icon' && kind ? kind : 'done'} colorClass={iconColor} />;
  } else if (glyph === 'dots' && !isError) {
    Glyph = <Dots />;
  } else if (glyph === 'icon' || isError) {
    Glyph = <Icon glyphKey={status} colorClass={iconColor} />;
  } else {
    Glyph = <Spinner colorClass={iconColor} />;
  }

  const row = 'inline-flex min-w-0 max-w-full items-center gap-[var(--status-gap)]';
  const container =
    variant === 'bubble'
      ? cn(row, 'bg-bubble-bot text-bubble-bot-fg px-[var(--bubble-px)] py-[var(--bubble-py)]')
      : variant === 'pill'
        ? cn(
            row,
            'border border-accent-line bg-accent-soft rounded-[var(--status-pill-radius)]',
            'px-[var(--status-pill-px)] py-[var(--status-pill-py)]',
          )
        : cn(row, 'py-[var(--space-1)]');

  const bubbleRadius =
    variant === 'bubble'
      ? 'var(--bubble-radius) var(--bubble-radius) var(--bubble-radius) var(--bubble-radius-tail)'
      : undefined;

  return (
    <div className={cn('flex max-w-full items-center gap-[var(--space-2)]', className)}>
      {avatar}
      <div className={container} style={bubbleRadius ? { borderRadius: bubbleRadius } : undefined}>
        <span className="inline-flex flex-none items-center">{Glyph}</span>
        {/* key re-triggers the fade/slide on every status/label change */}
        <span
          key={status + '|' + text}
          role="status"
          aria-live="polite"
          title={durText ? `${text} · ${durText}` : text}
          className={cn(
            'min-w-0 truncate font-sans text-[length:var(--status-font)] leading-[var(--leading-snug)]',
            'animate-[lb-status-in_var(--duration-base)_var(--ease-out)]',
            isDone ? 'font-normal' : 'font-medium',
            isError
              ? 'text-[var(--color-status-error-fg)]'
              : isDone
                ? 'text-[var(--color-status-done-fg)]'
                : 'text-[var(--color-status-fg)]',
          )}
        >
          {text}
        </span>
        {durText ? (
          <span
            aria-hidden="true"
            className="flex-none font-mono text-[length:var(--text-xs)] tabular-nums text-[var(--color-status-done-fg)]"
          >
            · {durText}
          </span>
        ) : startedAt != null && !isDone && !isError ? (
          <LiveDuration startedAt={startedAt} unit={labels.done?.unit} />
        ) : null}
      </div>
    </div>
  );
}
